/**
 * FungibleLp Strategy
 */
import { Decimal } from 'decimal.js';
import { BaseStrategy } from './strategy.js';
import BN from 'bn.js';
import { ClmmPoolUtil, TickMath } from '@cetusprotocol/common-sdk';
import { CLOCK_PACKAGE_ID, DISTRIBUTOR_OBJECT_ID, GLOBAL_CONFIGS, STSUI, SUI_SYSTEM_STATE, VERSIONS, } from '../utils/constants.js';
/**
 * FungibleLp Strategy for dual-asset liquidity pools with fungible tokens
 */
export class FungibleLpStrategy extends BaseStrategy {
    poolLabel;
    poolObject;
    investorObject;
    parentPoolObject;
    xTokenBalance = new Decimal(0);
    context;
    constructor(poolLabel, poolObject, investorObject, parentPoolObject, context) {
        super();
        this.poolLabel = poolLabel;
        this.poolObject = this.parsePoolObject(poolObject);
        this.investorObject = this.parseInvestorObject(investorObject);
        this.parentPoolObject = this.parseParentPoolObject(parentPoolObject);
        this.context = context;
    }
    getPoolLabel() {
        return this.poolLabel;
    }
    updateReceipts(xTokenBalance) {
        this.xTokenBalance = xTokenBalance;
    }
    /**
     * Returns alpha mining data - FungibleLp pools do not support alpha mining
     */
    getAlphaMiningData() {
        return {
            poolId: this.poolLabel.poolId,
            accRewardsPerXtoken: [],
            xTokenSupply: '0',
            receipt: null,
        };
    }
    /**
     * Get the exchange rate for fungible token to underlying token ratio
     * Calculated as tokens_invested / treasury_cap.total_supply
     */
    exchangeRate() {
        const tokensInvested = new Decimal(this.poolObject.tokensInvested);
        const totalSupply = new Decimal(this.poolObject.treasuryCap.totalSupply);
        if (totalSupply.isZero()) {
            return new Decimal(1); // Default exchange rate when no tokens are supplied
        }
        return tokensInvested.div(totalSupply);
    }
    /**
     * Get comprehensive pool data including TVL, LP breakdown, price, and position range
     */
    async getData() {
        const [alphafi, parent, lpBreakdown, parentLpBreakdown, currentLPPoolPrice, positionRange, apr,] = await Promise.all([
            this.getTvl(),
            this.getParentTvl(),
            this.getLpBreakdown(),
            this.getParentLpBreakdown(),
            this.getCurrentLPPoolPrice(),
            this.getPositionRange(),
            this.context.getAprData(this.poolLabel.poolId),
        ]);
        return {
            poolId: this.poolLabel.poolId,
            strategyType: this.poolLabel.strategyType,
            coinAType: this.poolLabel.assetA.type,
            coinBType: this.poolLabel.assetB.type,
            poolName: this.poolLabel.poolName,
            apr,
            tvl: {
                alphafi,
                parent,
            },
            lpBreakdown,
            parentLpBreakdown,
            currentLPPoolPrice,
            positionRange,
        };
    }
    /**
     * Calculate total value locked using current asset prices and token amounts
     */
    async getTvl() {
        const coinTypeA = this.poolLabel.assetA.type;
        const coinTypeB = this.poolLabel.assetB.type;
        const priceA = await this.context.getCoinPrice(coinTypeA);
        const priceB = await this.context.getCoinPrice(coinTypeB);
        const { amountA, amountB } = await this.getTokenAmounts(this.poolObject.tokensInvested);
        const usdValue = amountA.mul(priceA).add(amountB.mul(priceB));
        return { tokenAmountA: amountA, tokenAmountB: amountB, usdValue };
    }
    /**
     * Calculate parent pool TVL from underlying protocol reserves
     */
    async getParentTvl() {
        const coinTypeA = this.poolLabel.assetA.type;
        const coinTypeB = this.poolLabel.assetB.type;
        const decimalsA = await this.context.getCoinDecimals(coinTypeA);
        const decimalsB = await this.context.getCoinDecimals(coinTypeB);
        const priceA = await this.context.getCoinPrice(coinTypeA);
        const priceB = await this.context.getCoinPrice(coinTypeB);
        const tokenAmountA = new Decimal(this.parentPoolObject.coinA).div(new Decimal(10).pow(decimalsA));
        const tokenAmountB = new Decimal(this.parentPoolObject.coinB).div(new Decimal(10).pow(decimalsB));
        const usdValue = tokenAmountA.mul(priceA).add(tokenAmountB.mul(priceB));
        return { tokenAmountA, tokenAmountB, usdValue };
    }
    /**
     * Calculate token A and B amounts from liquidity using Cetus CLMM SDK
     */
    async getTokenAmounts(liquidity) {
        const coinTypeA = this.poolLabel.assetA.type;
        const coinTypeB = this.poolLabel.assetB.type;
        const scalingA = new Decimal(10).pow(await this.context.getCoinDecimals(coinTypeA));
        const scalingB = new Decimal(10).pow(await this.context.getCoinDecimals(coinTypeB));
        const liquidityBN = new BN(new Decimal(liquidity).toFixed(0));
        const currentSqrtPriceBN = new BN(this.parentPoolObject.currentSqrtPrice);
        const upperBound = 443636;
        let lowerTick = this.investorObject.lowerTick;
        let upperTick = this.investorObject.upperTick;
        if (lowerTick > upperBound) {
            lowerTick = -~(lowerTick - 1);
        }
        if (upperTick > upperBound) {
            upperTick = -~(upperTick - 1);
        }
        const lowerSqrtPriceBN = TickMath.tickIndexToSqrtPriceX64(lowerTick);
        const upperSqrtPriceBN = TickMath.tickIndexToSqrtPriceX64(upperTick);
        const amounts = ClmmPoolUtil.getCoinAmountFromLiquidity(liquidityBN, currentSqrtPriceBN, lowerSqrtPriceBN, upperSqrtPriceBN, false);
        const amountA = new Decimal(amounts.coin_amount_a.toString()).div(scalingA);
        const amountB = new Decimal(amounts.coin_amount_b.toString()).div(scalingB);
        return { amountA, amountB };
    }
    /**
     * Get LP token breakdown showing individual asset amounts and total liquidity
     */
    async getLpBreakdown() {
        const liquidity = this.poolObject.tokensInvested;
        const { amountA, amountB } = await this.getTokenAmounts(liquidity);
        const totalLiquidity = new Decimal(liquidity).div(new Decimal(1e9));
        return {
            token1Amount: amountA,
            token2Amount: amountB,
            totalLiquidity,
        };
    }
    /**
     * Get parent pool LP breakdown from underlying protocol
     */
    async getParentLpBreakdown() {
        const coinTypeA = this.poolLabel.assetA.type;
        const coinTypeB = this.poolLabel.assetB.type;
        const decimalsA = await this.context.getCoinDecimals(coinTypeA);
        const decimalsB = await this.context.getCoinDecimals(coinTypeB);
        const token1Amount = new Decimal(this.parentPoolObject.coinA).div(new Decimal(10).pow(decimalsA));
        const token2Amount = new Decimal(this.parentPoolObject.coinB).div(new Decimal(10).pow(decimalsB));
        const totalLiquidity = new Decimal(this.parentPoolObject.liquidity).div(new Decimal(1e9));
        return { token1Amount, token2Amount, totalLiquidity };
    }
    /**
     * Get current LP pool price from tick index
     */
    async getCurrentLPPoolPrice() {
        const coinTypeA = this.poolLabel.assetA.type;
        const coinTypeB = this.poolLabel.assetB.type;
        const decimalsA = await this.context.getCoinDecimals(coinTypeA);
        const decimalsB = await this.context.getCoinDecimals(coinTypeB);
        let currentTick = this.parentPoolObject.currentTickIndex;
        const upperBound = 443636;
        if (currentTick > upperBound) {
            currentTick = -~(currentTick - 1);
        }
        const price = TickMath.tickIndexToPrice(currentTick, decimalsA, decimalsB);
        return new Decimal(price.toString());
    }
    /**
     * Get position price range from lower and upper tick bounds
     */
    async getPositionRange() {
        const coinTypeA = this.poolLabel.assetA.type;
        const coinTypeB = this.poolLabel.assetB.type;
        const decimalsA = await this.context.getCoinDecimals(coinTypeA);
        const decimalsB = await this.context.getCoinDecimals(coinTypeB);
        const upperBound = 443636;
        let lowerTick = this.investorObject.lowerTick;
        let upperTick = this.investorObject.upperTick;
        if (lowerTick > upperBound) {
            lowerTick = -~(lowerTick - 1);
        }
        if (upperTick > upperBound) {
            upperTick = -~(upperTick - 1);
        }
        const lower = TickMath.tickIndexToPrice(lowerTick, decimalsA, decimalsB);
        const upper = TickMath.tickIndexToPrice(upperTick, decimalsA, decimalsB);
        return { lowerPrice: new Decimal(lower.toString()), upperPrice: new Decimal(upper.toString()) };
    }
    /**
     * Calculate user's current pool balance from fungible xToken balance
     * Uses wallet balance instead of receipts
     */
    async getBalance(_userAddress) {
        if (this.xTokenBalance.isZero()) {
            return {
                tokenAAmount: new Decimal(0),
                tokenBAmount: new Decimal(0),
                usdValue: new Decimal(0),
            };
        }
        const exchangeRate = this.exchangeRate();
        const tokens = this.xTokenBalance.mul(exchangeRate);
        const coinTypeA = this.poolLabel.assetA.type;
        const coinTypeB = this.poolLabel.assetB.type;
        const [priceA, priceB] = await Promise.all([
            this.context.getCoinPrice(coinTypeA),
            this.context.getCoinPrice(coinTypeB),
        ]);
        const { amountA, amountB } = await this.getTokenAmounts(tokens.floor().toString());
        const usdValue = amountA.mul(priceA).add(amountB.mul(priceB));
        return { tokenAAmount: amountA, tokenBAmount: amountB, usdValue };
    }
    getLiquidity(amount, isAmountA) {
        const upperBound = 443636;
        let lowerTick = this.investorObject.lowerTick;
        let upperTick = this.investorObject.upperTick;
        if (lowerTick > upperBound) {
            lowerTick = -~(lowerTick - 1);
        }
        if (upperTick > upperBound) {
            upperTick = -~(upperTick - 1);
        }
        const currentSqrtPriceBN = new BN(this.parentPoolObject.currentSqrtPrice);
        return ClmmPoolUtil.estLiquidityAndCoinAmountFromOneAmounts(lowerTick, upperTick, new BN(`${Math.floor(parseFloat(amount))}`), isAmountA, false, 0.5, currentSqrtPriceBN);
    }
    getOtherAmount(amount, isAmountA) {
        const liquidity = this.getLiquidity(amount, isAmountA);
        return [liquidity.coin_amount_a.toString(), liquidity.coin_amount_b.toString()];
    }
    coinAmountToXToken(amount, isAmountA) {
        const liquidity = new Decimal(this.getLiquidity(amount, isAmountA).liquidity_amount.toString());
        const exchangeRate = this.exchangeRate();
        return liquidity.div(exchangeRate).floor().toString();
    }
    /**
     * Parse pool object from blockchain response
     */
    parsePoolObject(response) {
        return this.safeParseObject(() => {
            const fields = this.extractFields(response);
            return {
                depositFee: this.getStringField(fields, 'deposit_fee'),
                depositFeeMaxCap: this.getStringField(fields, 'deposit_fee_max_cap'),
                id: this.getStringField(fields, 'id'),
                paused: this.getBooleanField(fields, 'paused', false),
                treasuryCap: {
                    id: this.getNestedField(fields, 'treasury_cap.id'),
                    totalSupply: this.getNestedField(fields, 'treasury_cap.total_supply.value'),
                },
                tokensInvested: this.getStringField(fields, 'tokensInvested'),
                withdrawFeeMaxCap: this.getStringField(fields, 'withdraw_fee_max_cap'),
                withdrawalFee: this.getStringField(fields, 'withdrawal_fee'),
            };
        }, 'Failed to parse FungibleLp pool object');
    }
    /**
     * Parse investor object from blockchain response
     */
    parseInvestorObject(response) {
        return this.safeParseObject(() => {
            const fields = this.extractFields(response);
            return {
                emergencyBalanceA: this.getStringField(fields, 'emergency_balance_a'),
                emergencyBalanceB: this.getStringField(fields, 'emergency_balance_b'),
                freeBalanceA: this.getStringField(fields, 'free_balance_a'),
                freeBalanceB: this.getStringField(fields, 'free_balance_b'),
                freeRewards: (() => {
                    const idVal = this.getNestedField(fields, 'free_rewards.id');
                    const sizeVal = this.getNestedField(fields, 'free_rewards.size');
                    return { id: String(idVal), size: String(sizeVal) };
                })(),
                id: this.getStringField(fields, 'id'),
                isEmergency: this.getBooleanField(fields, 'is_emergency', false),
                lowerTick: this.getNumberField(fields, 'lower_tick'),
                minimumSwapAmount: this.getStringField(fields, 'minimum_swap_amount'),
                performanceFee: this.getStringField(fields, 'performance_fee'),
                performanceFeeMaxCap: this.getStringField(fields, 'performance_fee_max_cap'),
                upperTick: this.getNumberField(fields, 'upper_tick'),
            };
        }, 'Failed to parse FungibleLp investor object');
    }
    /**
     * Parse parent pool object from blockchain response
     */
    parseParentPoolObject(response) {
        return this.safeParseObject(() => {
            const fields = this.extractFields(response);
            return {
                coinA: this.getStringField(fields, 'coin_a'),
                coinB: this.getStringField(fields, 'coin_b'),
                currentSqrtPrice: this.getStringField(fields, 'current_sqrt_price'),
                currentTickIndex: this.getNestedField(fields, 'current_tick_index.bits'),
                id: this.getStringField(fields, 'id'),
                liquidity: this.getStringField(fields, 'liquidity'),
            };
        }, 'Failed to parse FungibleLp parent pool object');
    }
    /**
     * Parse receipt objects (not applicable for FungibleLp)
     */
    parseReceiptObjects(_responses) {
        throw new Error('FungibleLp strategy does not have receipts');
    }
    async deposit(tx, options) {
        if (options.isAmountA === undefined) {
            throw new Error('isAmountA is required for AutobalanceLp strategy');
        }
        const [amountA, amountB] = this.getOtherAmount(options.amount.toString(), options.isAmountA);
        // get Coin Objects
        const depositCoinA = this.context.blockchain.getCoinObject(tx, this.poolLabel.assetA.type, options.address, BigInt(amountA));
        const depositCoinB = this.context.blockchain.getCoinObject(tx, this.poolLabel.assetB.type, options.address, BigInt(amountB));
        const [blueCoin] = await this.context.getCoinsBySymbols(['BLUE']);
        tx.moveCall({
            target: `${this.poolLabel.packageId}::alphafi_bluefin_stsui_sui_ft_pool::user_deposit`,
            typeArguments: [
                this.poolLabel.assetA.type,
                this.poolLabel.assetB.type,
                this.poolLabel.fungibleCoin.type,
                blueCoin.coinType,
            ],
            arguments: [
                tx.object(VERSIONS.FUNGIBLE_LP),
                tx.object(this.poolLabel.poolId),
                depositCoinA,
                depositCoinB,
                tx.object(DISTRIBUTOR_OBJECT_ID),
                tx.object(this.poolLabel.investorId),
                tx.object(GLOBAL_CONFIGS.BLUEFIN),
                tx.object(this.poolLabel.parentPoolId),
                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('BLUE', 'SUI', 'bluefin')),
                tx.object(STSUI.LST_INFO),
                tx.object(SUI_SYSTEM_STATE),
                tx.object(CLOCK_PACKAGE_ID),
            ],
        });
    }
    async withdraw(tx, options) {
        if (options.isAmountA === undefined) {
            throw new Error('isAmountA is required for AutobalanceLp strategy');
        }
        if (this.xTokenBalance.isZero()) {
            throw new Error('No xToken balance found!');
        }
        let xTokenAmount = '0';
        if (options.withdrawMax) {
            xTokenAmount = this.xTokenBalance.toString();
        }
        else {
            xTokenAmount = this.coinAmountToXToken(options.amount.toString(), options.isAmountA);
        }
        const withdrawFungibleCoin = this.context.blockchain.getCoinObject(tx, this.poolLabel.fungibleCoin.type, options.address, BigInt(xTokenAmount));
        const [blueCoin] = await this.context.getCoinsBySymbols(['BLUE']);
        tx.moveCall({
            target: `${this.poolLabel.packageId}::alphafi_bluefin_stsui_sui_ft_pool::user_withdraw`,
            typeArguments: [
                this.poolLabel.assetA.type,
                this.poolLabel.assetB.type,
                this.poolLabel.fungibleCoin.type,
                blueCoin.coinType,
            ],
            arguments: [
                tx.object(VERSIONS.FUNGIBLE_LP),
                withdrawFungibleCoin,
                tx.object(this.poolLabel.poolId),
                tx.object(DISTRIBUTOR_OBJECT_ID),
                tx.object(this.poolLabel.investorId),
                tx.object(GLOBAL_CONFIGS.BLUEFIN),
                tx.object(this.poolLabel.parentPoolId),
                tx.object(await this.context.getPoolIdBySymbolsAndProtocol('BLUE', 'SUI', 'bluefin')),
                tx.object(STSUI.LST_INFO),
                tx.object(SUI_SYSTEM_STATE),
                tx.object(CLOCK_PACKAGE_ID),
            ],
        });
    }
    async claimRewards(_tx, _alphaReceipt) {
        return;
    }
}
//# sourceMappingURL=fungibleLp.js.map